import { ConversationBuilderLocale } from "@/locales/localeid";
import { ConversationBuilderState } from "@/modules/conversation-builder";
import { getActualFileNameAndType } from "@/util/func";
import { Button, Form, Icon, Input, InputNumber, Modal, Progress, Slider, Upload } from "ant-design-vue";
import { WrappedFormUtils } from "ant-design-vue/types/form/form";
import { ActionItem, fmtMsg } from "commonui";
import { Component, Vue, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { EntityLengths } from "../../conversation-builder-util";
import { Conversation } from "../../conversation-builder.model";
import IConversationImageEditor from "../conversation-image-editor/conversation-image-editor";
import ConversationImageEditor from "../conversation-image-editor/conversation-image-editor.vue";
@Component({
	components: {
		Form,
		FormItem: Form.Item,
		InputNumber,
		Input,
		TextArea: Input.TextArea,
		Upload,
		Button,
		Icon,
		ConversationImageEditor,
		ActionItem,
		Modal,
		Progress,
		Slider
	}
})
export default class StartProperties extends Vue {
	$refs!: {
		imageEditor: IConversationImageEditor;
	};

	public form!: WrappedFormUtils;
	public doNotEmitChange: boolean = false;

	public conversationStore = getModule(ConversationBuilderState);
	public locale = ConversationBuilderLocale;

	created() {
		this.form = this.$form.createForm(this, {
			name: "start-properties",
			onValuesChange: this.onValuesChange
		});
	}

	mounted() {
		this.setValues();
	}

	@Watch("conversationStore.conversationProperties.name")
	onChange() {
		if (this.conversationStore.conversationProperties.name !== this.form.getFieldValue("name")) {
			this.form.setFieldsValue({ ["name"]: this.conversationStore.conversationProperties.name });
		}
	}

	@Watch("conversationStore.isConvNameUnique", { immediate: true })
	setNameValidation() {
		if (!this.conversationStore.isConvNameUnique) {
			this.form.setFields({
				["name"]: {
					value: this.conversationStore.conversationProperties.name,
					errors: [new Error(fmtMsg(ConversationBuilderLocale.ConversationAllreadyExits))]
				}
			});
		}
	}

	@Watch("conversationStore.isConvNameLengthValid", { immediate: true })
	setNameLengthValidation() {
		if (!this.conversationStore.isConvNameLengthValid) {
			this.form.setFields({
				["name"]: {
					value: this.conversationStore.conversationProperties.name,
					errors: [new Error(fmtMsg(ConversationBuilderLocale.ConversationNameLengthInvalid))]
				}
			});
		}
	}

	@Watch("conversationStore.isConvNameValid", { immediate: true })
	setNameCharacterValidation() {
		if (!this.conversationStore.isConvNameValid) {
			this.form.setFields({
				["name"]: {
					value: this.conversationStore.conversationProperties.name,
					errors: [new Error(fmtMsg(ConversationBuilderLocale.ConversationNameInvalid))]
				}
			});
		}
	}

	public get node(): Conversation {
		return this.conversationStore.conversationProperties;
	}

	public get entityLengths() {
		return EntityLengths.app;
	}

	public get canTestUXConversation() {
		return this.conversationStore.isUXTestingAllowed;
	}

	private setValues() {
		this.doNotEmitChange = true;
		const { name, description } = this.conversationStore.conversationProperties;
		this.form.setFieldsValue({
			name,
			description
		});
	}

	private onValuesChange(props: any, fields: Partial<Conversation>): void {
		if (this.doNotEmitChange) {
			this.doNotEmitChange = false;
			return;
		}
		!this.conversationStore.changedSinceLastSave && this.conversationStore.setChangedSinceLastSave(true);
		this.conversationStore.setConversationProps({
			...this.conversationStore.conversationProperties,
			...fields
		});
	}

	public async checkUniqueness(rule, value: string, callback: (msg?: string) => void) {
		if (!(await this.conversationStore.isNameUnique(value))) {
			callback(fmtMsg(ConversationBuilderLocale.ConversationAllreadyExits));
		}
		callback();
	}

	public openImageEditor() {
		const pageMeta = this.conversationStore.conversationProperties?.pageMetaData;
		this.$refs.imageEditor.startEditing(pageMeta ? pageMeta : undefined);
	}

	public get imageUrl(): string {
		return this.node.imageUrl!;
	}

	async downloadImage() {
		this.conversationStore.downloadFile(this.imageUrl);
	}

	public removeImage() {
		const remove = async () => {
			await this.conversationStore.deleteImage();
		};
		Modal.confirm({
			title: fmtMsg(ConversationBuilderLocale.ConversationDialogImageRemoveMsg),
			onOk: remove
		});
	}

	isImageUploadInProgress() {
		const node = this.node;
		return this.conversationStore.fileUploads.find((f) => node && f.nodeId === node.id);
	}

	get imageUploadProgress() {
		const uploadInfo = this.isImageUploadInProgress();
		if (uploadInfo) {
			return Math.floor((uploadInfo.uploaded * 100) / uploadInfo.size);
		}
	}

	public get getImageName(): string | void {
		if (this.imageUrl) {
			return getActualFileNameAndType(this.imageUrl);
		}
	}

	public speedChanged(speed: number) {
		this.conversationStore.setCurrentSpeed(speed);
	}
}
